import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  OnDestroy,
  ViewEncapsulation,
} from "@angular/core";
import * as Highcharts from "highcharts";
import { Subscription } from "rxjs";
import { AppService } from "src/app/app.service";
import { PharmaDataService } from "../pharma-data.service";
import {
  ColumnChartOptions,
  DrugsByCompanyApiResponse,
  GlobalHighchartOptions,
  ChartStateColors,
} from "../pharma-page.model";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";
import { FilterEventChanged } from "src/app/store/actions/pharma-page.actions";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
@Component({
  selector: "app-drugs-by-company",
  templateUrl: "./drugs-by-company.component.html",
  styleUrls: ["./drugs-by-company.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DrugsByCompanyComponent implements OnInit, OnDestroy {
  chartFilters;
  facetFilters;
  highcharts = Highcharts;
  chartOptions: Highcharts.Options;
  chartReady = false;
  showError = false;
  errorMessage = "";
  updateFlag = false;
  chart: Highcharts.Chart;
  totalCount = 0;
  tickersInfo: any;
  selectedRank = 0;
  selectedCompanyId;
  lastCalled: any = {};
  dataCallSubscription: Subscription;
  pharmaDatabasePageSubscriber: Subscription;
  @Output() onChartInstance: EventEmitter<Highcharts.Chart> =
    new EventEmitter();
  gridSettings;
  constructor(
    private service: PharmaDataService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
    private store: Store<AppState>,
  ) {
    this.iconRegistry.registerIcons([infoCircle]);
  }
  // removed $event here, since it was breaking in build
  @HostListener("window:resize", [])
  sizeChange() {
    this.updateFlag = true;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
    }
  }

  ngOnInit() {
    Highcharts.setOptions(GlobalHighchartOptions);
    this.service.onGlobalGridSettings.subscribe((resp) => {
      this.gridSettings = resp;
    });
    this.pharmaDatabasePageSubscriber = this.store
      .select(stateGetter("pharmaDatabasePage"))
      .subscribe((data: any) => {
        this.chartFilters = data.chartFilters;
        this.facetFilters = data.facetFilters;
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged === "chart" &&
          !data.filterEvent.source.includes("company")
        ) {
          this.facetFilters = data.lastFacetFilter;
        }
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged !== "grid" &&
          data.filterEvent.source !== "company-selected"
        ) {
          this.chartReady = false;
          this.getChartData();
        }
      });
  }

  callGetChartData(currentChartFilters) {
    if (
      this.appService.compare(currentChartFilters, this.lastCalled.chart_filter)
    )
      return false;
    return true;
  }

  getChartData() {
    const params = {
      chart_filter: { ...this.chartFilters },
      filter: { ...this.facetFilters },
      revenue_type: this.gridSettings
        ? this.gridSettings.options.data.revenue_type
        : "total_revenue",
    };
    this.lastCalled = params;
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    this.dataCallSubscription = this.service
      .getDrugsByCompanyData(params)
      .subscribe((response: DrugsByCompanyApiResponse) => {
        if (response.status === "success") {
          this.convertDataToChartObj(response.data);
          this.totalCount = response.data.total;
          this.selectedRank = response.data.selected_ticker_rank;
          this.selectedCompanyId = params.chart_filter.company_id;
          this.showError = false;
        } else {
          this.errorMessage = response.message;
          this.showError = true;
        }

        setTimeout(() => {
          this.chartReady = true;
        }, 0);
      });
  }

  convertDataToChartObj(dataObj) {
    const categories = dataObj.values.map((val) => val[0]);
    const series = dataObj.values;
    this.tickersInfo = dataObj.tickers;

    window.tickersInfo = this.tickersInfo;
    this.chartOptions = this.appService.superClone(ColumnChartOptions);
    this.chartOptions.yAxis["labels"]["style"] = { width: 72 };
    this.chartOptions.xAxis["categories"] = categories;
    this.chartOptions.series[0]["data"] = this.setSelectedOnLoad(series);
    if (this.chartFilters.company_id === "")
      this.chartOptions.plotOptions.series.color = ChartStateColors.selected;
    this.chartOptions.plotOptions.series.events = {
      click: (event) => {
        this.clickOnChart(event);
      },
    };

    this.chartOptions.xAxis = {
      ...this.chartOptions.xAxis,
      labels: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          return `<span class="custom" title="${
            window.tickersInfo[this.value].full_name
          }">${this.value} </span>`;
        },
      },
    };
  }

  clickOnChart(data) {
    if (data.point.y === 0) {
      //Block 0 count click on chart
      return;
    }
    const tickerObject = this.tickersInfo[data.point.category];
    if (tickerObject.company_id === this.chartFilters.company_id) {
      this.chartFilters.company_id = "";
      this.lastCalled.chart_filter.company_id = "";

      this.chartOptions.plotOptions.series.color = ChartStateColors.selected;
      this.chartOptions.colors = [ChartStateColors.selected];
      //deselect all
      const series = this.chartOptions.series[0]["data"];
      this.chartOptions.series[0]["data"] = series.map((val) => {
        if (val.selected) val.selected = false;
        return val;
      });
      this.updateFlag = true;
      this.store.dispatch(
        new FilterEventChanged({
          filterChanged: "chart",
          source: "company-deselected",
          data: {
            chartFilters: this.chartFilters,
            extraInfo: {
              ticker_data: {
                [tickerObject.company_id]: data.point.category,
              },
            },
          },
        }),
      );
      return;
    }
    //update filter and emit event for other charts
    this.lastCalled.chart_filter.company_id = tickerObject.company_id;
    this.chartFilters = JSON.parse(
      JSON.stringify({
        ...this.chartFilters,
        company_id: tickerObject.company_id,
      }),
    );
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "chart",
        source: "company-selected",
        data: {
          chartFilters: this.chartFilters,
          extraInfo: {
            ticker_data: {
              [tickerObject.company_id]: data.point.category,
            },
          },
        },
        //value: {'moa':'gene'}
      }),
    );
    //change color of unselected bars
    this.chartOptions.plotOptions.series.color = ChartStateColors.deselected;
    this.updateFlag = true;
  }

  setSelectedOnLoad(series) {
    if (this.chartFilters.company_id) {
      let ticker = "";
      for (const val in this.tickersInfo) {
        if (Object.prototype.hasOwnProperty.call(this.tickersInfo, val)) {
          const element = this.tickersInfo[val];
          if (element.company_id === this.chartFilters.company_id) {
            ticker = val;
            break;
          }
        }
      }

      const newSeries = series.map((val, index) => {
        const obj = {
          y: val[1],
          x: index,
          selected: false,
        };
        if (val[0] === ticker) {
          obj.selected = true;
          return obj;
        }
        return obj;
      });
      this.chartOptions.plotOptions.series.color = ChartStateColors.deselected;

      return newSeries;
    }
    return series;
  }

  ngOnDestroy() {
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    if (this.pharmaDatabasePageSubscriber) {
      this.pharmaDatabasePageSubscriber.unsubscribe();
    }
  }
}
